import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import api from "@/lib/api";
import { Loader2 } from "lucide-react";

function slugify(s) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 32);
}

export default function Register() {
  const { registerOwner } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", store_name: "", store_slug: "" });
  const [slugTouched, setSlugTouched] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onStoreName = (v) => {
    set("store_name", v);
    if (!slugTouched) set("store_slug", slugify(v));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!agreed) { setError("Please accept the agreements to continue."); return; }
    setError("");
    setLoading(true);
    try {
      await registerOwner(form);
      try { await api.post("/legal/accept"); } catch {}
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-md">
        <Logo size="lg" className="mb-8 justify-center" />
        <div className="rounded-2xl border border-border bg-card p-8 shadow-sm">
          <h1 className="text-2xl font-bold tracking-tight">Create your store</h1>
          <p className="text-muted-foreground mt-1.5 text-sm">Start selling in minutes. No card required.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="store_name">Store name</Label>
              <Input id="store_name" required value={form.store_name} onChange={(e) => onStoreName(e.target.value)}
                placeholder="Acme Research Supplies" data-testid="register-store-name" className="h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="store_slug">Store URL</Label>
              <div className="flex items-center rounded-lg border border-input bg-background overflow-hidden focus-within:ring-2 focus-within:ring-primary">
                <span className="pl-3 pr-1 text-sm text-muted-foreground">drelik.com/store/</span>
                <input id="store_slug" required value={form.store_slug}
                  onChange={(e) => { setSlugTouched(true); set("store_slug", slugify(e.target.value)); }}
                  placeholder="acme" data-testid="register-store-slug"
                  className="flex-1 h-11 bg-transparent outline-none text-sm pr-3" />
              </div>
            </div>
            <div className="border-t border-border my-2" />
            <div className="space-y-2">
              <Label htmlFor="name">Your name</Label>
              <Input id="name" required value={form.name} onChange={(e) => set("name", e.target.value)}
                placeholder="Jane Doe" data-testid="register-name" className="h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)}
                placeholder="you@company.com" data-testid="register-email" className="h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={6} value={form.password}
                onChange={(e) => set("password", e.target.value)} placeholder="At least 6 characters"
                data-testid="register-password" className="h-11" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="h-4 w-4 rounded mt-0.5 shrink-0" data-testid="register-agree-checkbox" />
              <span className="text-xs text-muted-foreground leading-relaxed">
                I have read and agree to the Drelik{" "}
                <a href="/legal/agreement" target="_blank" rel="noreferrer" className="text-primary hover:underline">Store Owner Agreement</a>,{" "}
                <a href="/legal/terms" target="_blank" rel="noreferrer" className="text-primary hover:underline">Terms &amp; Conditions</a>,{" "}
                <a href="/legal/privacy" target="_blank" rel="noreferrer" className="text-primary hover:underline">Privacy Policy</a>, and{" "}
                <a href="/legal/aup" target="_blank" rel="noreferrer" className="text-primary hover:underline">Acceptable Use Policy</a>.
              </span>
            </label>
            <Button type="submit" disabled={loading || !agreed} className="w-full h-11 rounded-lg" data-testid="register-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create store"}
            </Button>
          </form>
        </div>
        <p className="mt-6 text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" className="text-primary font-medium hover:underline" data-testid="register-to-login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
